import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { animateScroll } from 'react-scroll';

import Switch from 'react-switch';
import PropTypes from 'prop-types';
import { toNumber } from 'lodash';
import { Button, Card, CardBody, CardText, CardTitle, Col, Input, Row, TabContent, TabPane } from 'reactstrap';
import SummaryModal from '../Vendor/Modal';
import TabNav from '../section/TabNav';
import TabOne from '../section/TabOne';
import BZAPIService from '../../../services/BZAPIService';
import CMDBAffectedCIQuery from '../ServiceNow/CMDBAffectedCIQuery';
import CMDBCompany from '../ServiceNow/CMDBCompany';
import CMDBCompanyCheck from '../ServiceNow/CMDBCompanyCheck';
import ProductPriority from '../section/ProductPriority';
import VendorDaysBack from '../Vendor/VendorsDaysBack';
import HistoryTab from '../section/HistoryTab';

// constants
import SETTINGS from '../../../constants/settings';

const ConfigFormPostgreSQL = ({
  isReadOnly,
  fields,
  handleFieldChange,
  vendorLabel,
  vendorId,
  bugsDateBack,
  setBugsDateBack,
  vendorPriorityOptions,
  vendorStatusOptions,
  vendorResolutionOptions,
  snIntegrationCheck,
  services,
  vendors,
  priorityStats,
  setPriorityStats,
  snCompanySysIdOptions,
  snCompanySysIdErrors,
  vendorStatuses,
  setVendorStatuses,
  vendorResolutions,
  setVendorResolutions,
  priorityMaps,
  setPriorityMaps,
  snPriorityOptions,
  keywords,
  setKeywords,
  searchFields,
  setSearchFields,
  snCompanySysId,
  setSnCompanySysId,
  setServices,
  setVendors,
  buildVendorStat,
  setBugs,
  isConfigured,
  syncOnly,
  setSyncOnly,
  loading,
  isServiceActive,
  setIsServiceActive,
  allRequired,
  setAllRequired,
  isUpdated,
  setIsUpdated
}) => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [activeTab, setActiveTab] = useState('1');
  const [modalOpen, setModalOpen] = useState(false);
  const [validated, setValidated] = useState(null);
  const [isForceRun, setIsForceRun] = useState(false);
  const [nextRun, setNextRun] = useState();
  const [timeUnit, setTimeUnit] = useState('');
  const [timeValue, setTimeValue] = useState({});

  async function setBugServiceFrequency() {
    // Check for time unit, post frequency
    const data = {
      timeUnit,
      number: toNumber(timeValue.value)
    };
    if (vendorId.length) {
      try {
        const res = await BZAPIService.postBugServiceSchedule(vendorId, data, { method: 'POST' });
        setNextRun(res?.data?.nextRunUTC);
        // console.log('res', res);
      } catch (e) {
        console.log(e);
      }
    }
  }

  const navigate = useNavigate();

  async function saveConfig() {
    console.log('saveConfig', fields);
    const vendorPriorities = priorityMaps.map(mapping => {
      return {
        value: mapping.vendorPriority.value,
        label: mapping.vendorPriority.value,
        snPriority: mapping.snPriority.value
      };
    });

    const data = {
      bugsDateBack: bugsDateBack,
      snCompanySysId: snCompanySysId.value ? snCompanySysId.value : fields.snCompanySysId,
      snAffectedCIQuery: fields.snAffectedCIQuery,
      vendorPriorities,
      vendorStatuses,
      vendorResolutions,
      excludedKws: keywords,
      searchFields: searchFields,

      type: SETTINGS.VENDOR
    };

    return BZAPIService.saveSetting(vendorId, syncOnly, data, isForceRun);
  }

  async function handleSave() {
    try {
      setIsLoading(true);
      setIsSubmitted(true);
      // Validate
      if (!bugsDateBack.length || !snCompanySysId) {
        setValidated(false);
        setActiveTab('3');
        setIsLoading(false);
        return;
      }
      if (!priorityMaps.length) {
        setIsLoading(false);
        setValidated(false);
        setActiveTab('4');
        return;
      }

      await saveConfig();
      setBugServiceFrequency();
      setIsLoading(false);
      setIsSubmitted(false);
      setModalOpen(false);
      setIsForceRun(false);
      setActiveTab('1');
      toast.success(`Settings Saved!`);
    } catch (e) {
      setIsLoading(false);
      toast.error('Failed to save.');
      console.log('e', e);
    }
  }

  const handleStatusVariableChange = (e, variable, option) => {
    const num = e.target.value;
    // const max = e.target.max;
    // console.log('num', num);
    // console.log('max', max);

    const options = [...vendorStatuses];
    const variables = [...option.variables];

    // const statuses = vendorStatuses.map(item => {
    //   return item.label === option.label ? update : item;
    // });

    const optionIndex = vendorStatuses.findIndex(item => {
      if (item.label === option.label) return true;
    });

    const varsIndex = option.variables.findIndex(item => {
      if (item.label === variable.label) return true;
    });

    variables[varsIndex] = { ...variables[varsIndex], value: Number(num) };
    options[optionIndex] = { ...options[optionIndex], variables };

    setVendorStatuses(options);
    // console.log('options', options);
  };

  const handleSwitchToggle = (e, option) => {
    // const check = e.target.checked;
    // console.log(e);

    const options = [...vendorStatuses];
    const optionIndex = vendorStatuses.findIndex(item => {
      if (item.label === option.label) return true;
    });

    options[optionIndex] = { ...options[optionIndex], active: e === true ? 1 : 0 };

    setVendorStatuses(options);
  };

  function handleValidationError() {
    setModalOpen(!modalOpen);
    animateScroll.scrollTo(70);
  }

  useEffect(() => {
    setIsUpdated(true);
    if (
      bugsDateBack?.length &&
      snCompanySysId?.value &&
      priorityMaps?.length &&
      !priorityMaps.some(mapping => !mapping.snPriority || !mapping.vendorPriority)
    ) {
      setAllRequired(true);
    } else {
      setAllRequired(false);
    }
  }, [
    fields,
    bugsDateBack,
    priorityStats,
    vendorStatuses,
    vendorResolutions,
    priorityMaps,
    keywords,
    searchFields,
    snCompanySysId
  ]);

  return (
    <>
      <TabNav
        vendorId={vendorId}
        credentials={false}
        activeTab={activeTab}
        snIntegrationCheck={snIntegrationCheck}
        setActiveTab={setActiveTab}
        setIsLoading={setIsLoading}
        isLoading={isLoading}
        setServices={setServices}
        setVendors={setVendors}
        buildVendorStat={buildVendorStat}
        setPriorityStats={setPriorityStats}
        setBugs={setBugs}
        isConfigured={isConfigured}
      />

      <TabContent>
        <TabOne
          vendorId={vendorId}
          vendorLabel={vendorLabel}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
          snIntegrationCheck={snIntegrationCheck}
          services={services}
          vendors={vendors}
          priorityStats={priorityStats}
          isConfigured={isConfigured}
          modalOpen={modalOpen}
          setModalOpen={setModalOpen}
          setIsForceRun={setIsForceRun}
          timeUnit={timeUnit}
          setTimeUnit={setTimeUnit}
          timeValue={timeValue}
          setTimeValue={setTimeValue}
          nextRun={nextRun}
          setNextRun={setNextRun}
          loading={loading}
          isServiceActive={isServiceActive}
          setIsServiceActive={setIsServiceActive}
        />
        <form noValidate style={{ minWidth: '100%' }}>
          <TabPane tabId="3">
            {activeTab === '3' && (
              <Card bg={'light'} text={'dak'} className="mb-4">
                {/* <FalconCardHeader title="Vendor Settings" /> */}

                <CardBody>
                  <Row>
                    <Col lg="6">
                      <VendorDaysBack
                        bugsDateBack={bugsDateBack}
                        setBugsDateBack={setBugsDateBack}
                        isSubmitted={isSubmitted}
                        vendorId={vendorId}
                      />
                    </Col>
                    <Col lg="6">
                      {snCompanySysIdErrors === false ? (
                        <CMDBCompanyCheck
                          snCompanySysIdOptions={snCompanySysIdOptions}
                          setSnCompanySysId={setSnCompanySysId}
                          snCompanySysId={snCompanySysId}
                          isSubmitted={isSubmitted}
                        />
                      ) : (
                        <CMDBCompany
                          snCompanySysId={fields.snCompanySysId}
                          handleFieldChange={handleFieldChange}
                          isSubmitted={isSubmitted}
                        />
                      )}
                    </Col>
                  </Row>
                  <CMDBAffectedCIQuery
                    snAffectedCIQuery={fields.snAffectedCIQuery}
                    handleFieldChange={handleFieldChange}
                    isSubmitted={isSubmitted}
                  />
                  {!!vendorStatuses.length && vendorStatuses[0]?.hasOwnProperty('variables') ? (
                    <>
                      <CardTitle>Select Bug Statuses to Monitor</CardTitle>
                      <CardText>Select multiple entries if necessary</CardText>
                      <Row>
                        {vendorStatuses.map(option => {
                          return (
                            <Col lg="6" key={option.label + 'option'}>
                              <Card className="mt-2">
                                <CardBody>
                                  <Row>
                                    <Col xs={8}>
                                      <CardTitle>{option.label}</CardTitle>
                                    </Col>
                                    <Col xs={4} className="text-right">
                                      <Switch
                                        id={option.label + '-switch'}
                                        onChange={e => handleSwitchToggle(e, option)}
                                        checked={option.active === 1 ? true : false}
                                        uncheckedIcon={false}
                                        onColor="#617E71"
                                        height={14}
                                        width={28}
                                      />
                                    </Col>
                                  </Row>
                                  {option.variables.map(variable => {
                                    return (
                                      <Row
                                        key={variable.label + option.label + 'variableKey'}
                                        id={variable.label + option.label + 'variableId'}
                                        className="mt-2"
                                      >
                                        <Col>
                                          <strong>{variable.label}</strong>
                                        </Col>
                                        <Col>
                                          <Input
                                            placeholder="0"
                                            type="number"
                                            key={variable.label + 'input'}
                                            // autoFocus="autoFocus"
                                            value={variable.value}
                                            onChange={e => handleStatusVariableChange(e, variable, option)}
                                            disabled={option.active === 1 ? false : true}
                                            min={
                                              option.label === 'Active' &&
                                              option.active === 1 &&
                                              variable.label === 'Min Thread Responses'
                                                ? vendorStatuses[2].variables[0].value
                                                : option.label === 'Active' &&
                                                  option.active === 1 &&
                                                  variable.label === 'Max Days Updated'
                                                ? vendorStatuses[2].variables[1].value
                                                : 0
                                            }
                                            max={
                                              // Validation
                                              option.label === 'New' &&
                                              option.active === 1 &&
                                              variable.label === 'Max Thread Responses'
                                                ? vendorStatuses[1].variables[1].value
                                                : option.label === 'Inactive' &&
                                                  option.active === 1 &&
                                                  variable.label === 'Max Thread Responses'
                                                ? vendorStatuses[3].variables[0].value
                                                : option.label === 'Inactive' &&
                                                  option.active === 1 &&
                                                  variable.label === 'Min Days Updated'
                                                ? vendorStatuses[3].variables[1].value
                                                : null
                                            }
                                          />
                                        </Col>
                                      </Row>
                                    );
                                  })}
                                </CardBody>
                              </Card>
                            </Col>
                          );
                        })}
                      </Row>
                    </>
                  ) : (
                    ''
                  )}
                </CardBody>
              </Card>
            )}
          </TabPane>
          <TabPane tabId="4">
            {activeTab === '4' && (
              <ProductPriority
                title="Default Bug Configuration"
                vendorPriorityOptions={vendorPriorityOptions}
                vendorStatusOptions={vendorStatusOptions}
                vendorResolutionOptions={vendorResolutionOptions}
                // setVendorStatuses={setVendorStatuses}
                setVendorResolutions={setVendorResolutions}
                // vendorStatuses={vendorStatuses}
                vendorResolutions={vendorResolutions}
                priorityMaps={priorityMaps}
                setPriorityMaps={setPriorityMaps}
                snPriorityOptions={snPriorityOptions}
                keywords={keywords}
                setKeywords={setKeywords}
                searchFields={searchFields}
                setSearchFields={setSearchFields}
              />
            )}
          </TabPane>
          <TabPane tabId="6">{activeTab === '6' && <HistoryTab vendorId={vendorId} />}</TabPane>

          <Row className={`${activeTab === '1' ? 'd-none' : ''} flex-between-center`}>
            <Col xs="auto">
              <Button
                block
                color="falcon-danger"
                onClick={() => {
                  console.log(`/vendors/${vendorId}/configuration`);
                  navigate(-1);
                }}
              >
                Cancel
              </Button>
            </Col>
            <Col xs="auto">
              {/* <LoaderButton block color="falcon-primary" onClick={handleSave} name="save-and-add" isLoading={isLoading}>
                Save changes
              </LoaderButton> */}
              <SummaryModal
                modalOpen={modalOpen}
                isReadOnly={isReadOnly}
                setModalOpen={setModalOpen}
                handleSave={handleSave}
                isLoading={isLoading}
                vendorLabel={vendorLabel?.replace('Systems', 'Networks')}
                vendorId={vendorId}
                validated={validated}
                setValidated={setValidated}
                syncOnly={syncOnly}
                setSyncOnly={setSyncOnly}
                handleValidationError={handleValidationError}
                allRequired={allRequired}
                isUpdated={isUpdated}
              />
            </Col>
          </Row>
        </form>
      </TabContent>
    </>
  );
};
ConfigFormPostgreSQL.propTypes = {
  vendor: PropTypes.object,
  isReadOnly: PropTypes.bool,
  settings: PropTypes.object,
  vendorLabel: PropTypes.string,
  vendorId: PropTypes.string,
  vendorPriorityOptions: PropTypes.array,
  vendorStatusOptions: PropTypes.array,
  vendorStatuses: PropTypes.array,
  vendorResolutionOptions: PropTypes.array,
  snIntegrationCheck: PropTypes.bool,
  services: PropTypes.array,
  vendors: PropTypes.array,
  bugs: PropTypes.object,
  priorityStats: PropTypes.object,
  setPriorityStats: PropTypes.func,
  bugOptions: PropTypes.array,
  selectedBugOption: PropTypes.object,
  setSelectedBugOption: PropTypes.func,
  snCompanySysIdOptions: PropTypes.object,
  snCompanySysId: PropTypes.object,
  snCompanySysIdErrors: PropTypes.bool,
  vendorEventScopes: PropTypes.array,
  setVendorEventScopes: PropTypes.func,
  vendorResolutions: PropTypes.array,
  setVendorResolutions: PropTypes.func,
  setVendorStatuses: PropTypes.func,
  fields: PropTypes.object,
  handleFieldChange: PropTypes.func,
  priorityMaps: PropTypes.array,
  setPriorityMaps: PropTypes.func,
  snPriorityOptions: PropTypes.array,
  keywords: PropTypes.array,
  setKeywords: PropTypes.func,
  searchFields: PropTypes.array,
  setSearchFields: PropTypes.func,
  setSnCompanySysId: PropTypes.func,
  bugsDateBack: PropTypes.string,
  setBugsDateBack: PropTypes.func,
  setServices: PropTypes.func,
  setVendors: PropTypes.func,
  buildVendorStat: PropTypes.func,
  setBugs: PropTypes.func,
  isConfigured: PropTypes.bool,
  syncOnly: PropTypes.bool,
  setSyncOnly: PropTypes.func,
  loading: PropTypes.bool,
  setIsServiceActive: PropTypes.func,
  isServiceActive: PropTypes.number,
  allRequired: PropTypes.bool,
  isUpdated: PropTypes.bool,
  setAllRequired: PropTypes.func,
  setIsUpdated: PropTypes.func
};
export default ConfigFormPostgreSQL;
